import _reactGA4 from 'react-ga4';
import { ANALYTICS_ENABLED } from './analytics';

// Handles CJS/ESM interop: Rolldown production bundler may double-wrap the default export
const reactGA4 =
  (_reactGA4 as unknown as { default: typeof _reactGA4 })?.default ?? _reactGA4;

type EventParams = Record<string, string | number | boolean | undefined>;

export const AnalyticsEventName = {
  CLIENT_ERROR: 'client_error',
  API_REQUEST_FAILURE: 'api_request_failure',
  MODAL_OPEN: 'modal_open',
  IMAGE_UPLOAD_SUCCESS: 'image_upload_success',
  IMAGE_UPLOAD_FAILURE: 'image_upload_failure',
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEventName)[keyof typeof AnalyticsEventName];

export type UIInteractionName =
  | IfmInteraction
  | ProductImageInteraction
  | CategoryInteraction
  | ImageLibraryInteraction
  | AbCatalogInteraction
  | GeneralInteraction
  | TreeNodeInteraction;

type IfmInteraction =
  | 'ifm_category_card_clicked'
  | 'ifm_category_searched'
  | 'ifm_related_filters_toggled'
  | 'ifm_filter_deleted'
  | 'ifm_suggestions_viewed'
  | 'ifm_suggested_filter_selected'
  | 'ifm_filter_type_clicked'
  | 'ifm_market_changed'
  | 'ifm_notification_dismissed'
  | 'ifm_filter_search_select'
  | 'ifm_card_list_clicked'
  | 'ifm_filter_change_category_clicked'
  | 'ifm_link_clicked'
  | 'ifm_filter_market'
  | 'ifm_manage_filter_created'
  | 'ifm_manage_filter_create_error'
  | 'ifm_manage_filter_validated'
  | 'ifm_manage_filter_connected_global'
  | 'ifm_manage_filter_cancelled'
  | 'ifm_manage_filter_value_toggled'
  | 'ifm_manage_filter_disable_created_toggled'
  | 'ifm_manage_filter_multi_select_created_toggled'
  | 'ifm_suggested_filter_generate'
  | 'ifm_suggested_filter_regenerate'
  | 'ifm_suggested_filter_created'
  | 'ifm_suggested_filter_bulk_created'
  | 'ifm_manage_filter_message_action'
  | 'ifm_manage_filter_message_dismissed';

type ProductImageInteraction =
  | 'product_image_reset_confirmed'
  | 'product_image_reset_clicked'
  | 'product_image_search'
  | 'product_image_submit'
  | 'product_image_submit_error'
  | 'product_image_cancel'
  | 'product_category_image_toggle';

type CategoryInteraction =
  | 'category_save'
  | 'category_cancel'
  | 'category_unsaved_changes_overlay'
  | 'category_autocategorisable_toggled'
  | 'category_type_changed'
  | 'category_import_clicked'
  | 'category_rooms_slug_changed'
  | 'category_publish_toggled'
  | 'category_publish_date_changed'
  | 'category_translation_verified_toggled'
  | 'category_image_upload_click'
  | 'category_image_override_reason_select'
  | 'category_image_toggle'
  | 'category_image_cancel'
  | 'categorize_series_collections'
  | 'category_delete'
  | 'create_new_category';

type ImageLibraryInteraction =
  | 'image_library_search'
  | 'image_library_search_clear'
  | 'image_library_search_scope_change'
  | 'image_library_aspect_ratio_change'
  | 'image_library_view_details'
  | 'image_library_load_more'
  | 'image_library_back_to_search'
  | 'image_library_override_reason'
  | 'image_library_select_image';

type AbCatalogInteraction =
  | 'ab_catalog_variant_selected'
  | 'ab_catalog_apply_sort'
  | 'report_view_apply_sort'
  | 'create_ab_catalog'
  | 'delete_ab_catalog'
  | 'delete_draft_catalog';

type TreeNodeInteraction =
  | 'copy_category'
  | 'cut_category'
  | 'move_category'
  | 'paste_category'
  | 'paste_item'
  | 'load_more_items';

type GeneralInteraction =
  | 'properties_tab_click'
  | 'switched_catalog'
  | 'copy_item_no'
  | 'market_selector_close'
  | 'market_language_selected'
  | 'external_link_click'
  | 'catalog_property_value_changed'
  | 'change_market'
  | 'export_excel'
  | 'hotkey_escape'
  | 'properties_pane_open'
  | 'properties_pane_close'
  | 'delete_items'
  | 'user_profile_close'
  | 'user_logout'
  | 'hide_from_navigation'
  | 'toggle_theme'
  | 'skip_to_main'
  | 'logo_click'
  | 'activate_shops';

type AnalyticsPayloads = {
  client_error: { message: string; stack_hash?: string };
  api_request_failure: {
    path: string;
    method: string;
    status?: number;
    error_message?: string;
  };
  modal_open: { modal_name: string };
  image_upload_success: { file_size?: number; file_type?: string };
  image_upload_failure: { error_code?: string };
};

const send = <T extends keyof AnalyticsPayloads>(
  name: T,
  ...args: AnalyticsPayloads[T] extends undefined
    ? []
    : [params: AnalyticsPayloads[T]]
) => {
  const params = args[0];

  if (!ANALYTICS_ENABLED) {
    return;
  }

  try {
    reactGA4.event(name, params as Record<string, unknown>);
  } catch (err) {
    // swallow analytics errors
  }
};

export const trackClientError = (message: string, stack_hash?: string) =>
  send(AnalyticsEventName.CLIENT_ERROR, { message, stack_hash });

export const sanitizeAnalyticsPath = (url: string) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
};

export const trackApiRequestFailure = (
  path: string,
  method: string,
  options: {
    status?: number;
    errorMessage?: string;
  } = {},
) =>
  send(AnalyticsEventName.API_REQUEST_FAILURE, {
    path,
    method,
    status: options.status,
    error_message: options.errorMessage,
  });

export const trackModalOpen = (modalName: string) =>
  send(AnalyticsEventName.MODAL_OPEN, { modal_name: modalName });

export const trackImageUploadSuccess = (fileSize?: number, fileType?: string) =>
  send(AnalyticsEventName.IMAGE_UPLOAD_SUCCESS, {
    file_size: fileSize,
    file_type: fileType,
  });

export const trackImageUploadFailure = (errorCode?: string) =>
  send(AnalyticsEventName.IMAGE_UPLOAD_FAILURE, { error_code: errorCode });

export const trackUIInteraction = (
  interaction: UIInteractionName,
  params: EventParams = {},
) => {
  if (!ANALYTICS_ENABLED) return;
  try {
    reactGA4.event(interaction, params as Record<string, unknown>);
  } catch (err) {
    // swallow analytics errors
  }
};
